// ─── Aviso de versión nueva ──────────────────────────────────────────────────
// SIN sondeo periódico: el aviso llega por EMPUJE. La tabla app_version tiene
// una sola fila con el sello de la versión publicada (bundle.js?v=HASH, ver
// build.mjs) y está en la publicación de Supabase Realtime; actualizarla es el
// último paso del despliegue (ver CLAUDE.md). Cuando cambia, cada panel
// abierto recibe el evento al instante y muestra la tarjeta abajo a la
// derecha. NUNCA se recarga solo — la decisión es de quien está trabajando.
//
// Solo hay dos consultas puntuales, ambas por un hecho concreto y no por
// reloj: una al abrir el panel y una al volver a la pestaña, porque un
// websocket dormido (portátil cerrado, pestaña en segundo plano durante
// horas) pierde los mensajes emitidos mientras tanto.
//
// La tarjeta vive como hija directa de <body>, fuera de #root: render()
// reconstruye todo el DOM de la app en cada navegación y la barrería.
//
// Antes de recargar se llama a vaciarSincronizacionPendiente(): el guardado
// local es inmediato, pero la nube va con un rezago (debounce de 800ms y
// peticiones en vuelo) y una recarga en ese instante dejaría el último tramo
// de trabajo solo en este navegador.
import { vaciarSincronizacionPendiente } from "../core/cloudSync.js";
import { PRIORIDAD, anclarConEntrada, hacerArrastrable } from "./dock.js";
import { hayDocumentoAbierto, permitirSalida } from "./trabajoAbierto.js";
import { C } from "../core/config.js";
import { supa } from "../core/supabaseClient.js";

const REVANCHA_MS = 30 * 60 * 1000;   // "Más tarde" vuelve a avisar a la media hora

let versionActual = null;
let versionSilenciada = null;

export function iniciarAvisoDeVersion() {
  const propio = document.querySelector('script[src*="bundle.js"]');
  versionActual = propio ? (propio.getAttribute("src").match(/v=([a-f0-9]+)/) || [])[1] : null;
  if (!versionActual) return;   // build local sin sello: nada que comparar

  const evaluar = v => {
    if (v && v !== versionActual && v !== versionSilenciada
        && !document.getElementById("aviso-version")) mostrarAviso(v);
  };

  // Empuje: la fila cambia únicamente cuando se publica una versión nueva.
  supa.channel("app-version")
    .on("postgres_changes",
      { event: "*", schema: "public", table: "app_version" },
      payload => evaluar(payload.new && payload.new.version))
    .subscribe();

  const consultar = async () => {
    try {
      const { data } = await supa.from("app_version").select("version").eq("id", 1).maybeSingle();
      if (data) evaluar(data.version);
    } catch { /* sin red en este momento: el realtime avisará al reconectar */ }
  };
  document.addEventListener("visibilitychange", () => { if (!document.hidden) consultar(); });
  consultar();
}

function mostrarAviso(versionNueva) {
  const tarjeta = document.createElement("div");
  tarjeta.id = "aviso-version";
  tarjeta.style.cssText = `
    width:330px;max-width:calc(100vw - 32px);
    background:white;border:1px solid ${C.border};border-left:4px solid ${C.accent};
    border-radius:10px;box-shadow:0 12px 34px rgba(15,23,42,.18);padding:13px 14px;
    font-family:inherit;
  `;
  tarjeta.innerHTML = `
    <div style="display:flex;gap:9px;align-items:flex-start;">
      <div style="font-size:20px;line-height:1;">🔄</div>
      <div style="flex:1;min-width:0;">
        <div style="display:flex;align-items:center;gap:7px;">
          <div style="font-size:12.5px;font-weight:800;color:${C.text};flex:1;">Hay una versión nueva del panel</div>
          <span title="Arrastra para mover este aviso" style="font-size:13px;color:${C.muted};letter-spacing:1px;line-height:1;">⠿</span>
        </div>
        <div id="aviso-version-texto" style="font-size:10.5px;color:${C.muted};margin-top:3px;line-height:1.45;">
          Lo que ya está <b>guardado</b> queda a salvo: antes de recargar se termina
          de enviar a la nube lo pendiente. Si tienes un documento abierto sin
          guardar, guárdalo primero.
        </div>
        <div style="display:flex;gap:6px;margin-top:9px;">
          <button id="aviso-version-recargar" style="
            background:${C.mathiz};color:white;border:none;border-radius:6px;
            padding:6px 12px;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;
          ">🔄 Actualizar ahora</button>
          <button id="aviso-version-luego" style="
            background:transparent;color:${C.muted};border:1px solid ${C.border};border-radius:6px;
            padding:6px 12px;font-size:11px;font-weight:600;cursor:pointer;font-family:inherit;
          ">Más tarde</button>
        </div>
      </div>
    </div>
  `;
  anclarConEntrada(tarjeta, PRIORIDAD.version);
  // Se puede correr con el mouse (el arrastre está limitado a la ventana, así
  // que no se sale de la pantalla ni se pierde): el aviso se queda hasta que se
  // actualice, y a veces cae justo encima de lo que se está leyendo.
  hacerArrastrable(tarjeta);

  tarjeta.querySelector("#aviso-version-recargar").onclick = async (e) => {
    const btn = e.currentTarget;

    // Un documento abierto sin guardar NO sobrevive a la recarga: vive en
    // memoria, no en localStorage, y como nunca se guardó tampoco entra en
    // "documentos a medias". Antes se recargaba igual y se perdía lo escrito.
    // Ahora hay que insistir a sabiendas.
    if (hayDocumentoAbierto() && !btn.dataset.insistir) {
      btn.dataset.insistir = "1";
      btn.textContent = "Actualizar y descartar";
      btn.style.background = C.red;
      const texto = tarjeta.querySelector("#aviso-version-texto");
      if (texto) {
        texto.innerHTML = `<b style="color:${C.red};">Tienes un documento abierto sin guardar.</b>
          Guárdalo (o ciérralo) y vuelve a pulsar Actualizar. Si continúas ahora,
          se pierde lo que hay en pantalla: al no estar guardado, tampoco queda
          en "documentos a medias".`;
      }
      return;
    }

    btn.disabled = true;
    btn.textContent = "Guardando tu trabajo…";
    // Tope de 6s: si la nube no responde, se recarga igual — localStorage ya
    // tiene todo y quedarse colgado aquí sería peor que el rezago de sync.
    try {
      await Promise.race([
        vaciarSincronizacionPendiente(),
        new Promise(r => setTimeout(r, 6000))
      ]);
    } catch { /* la recarga procede igual */ }
    permitirSalida();   // esta recarga es a propósito: sin confirmación del navegador
    location.reload();
  };

  tarjeta.querySelector("#aviso-version-luego").onclick = () => {
    versionSilenciada = versionNueva;
    tarjeta.style.opacity = "0";
    tarjeta.style.transform = "translateY(8px)";
    setTimeout(() => tarjeta.remove(), 400);
    // El silencio caduca: media hora después vuelve a ofrecer la actualización.
    setTimeout(() => { if (versionSilenciada === versionNueva) versionSilenciada = null; }, REVANCHA_MS);
  };
}
